/**
 * CollecteMap — carte Leaflet des points de collecte (fond IGN Géoplateforme).
 * Wrapper léger autour de Leaflet (pas de react-leaflet, comme ParcelleMap).
 *
 * - Marqueurs colorés par type (pastille divIcon), popup au clic (nom, adresse,
 *   horaires, déchets acceptés).
 * - Mode « pose » (onPick) : cliquer sur la carte remonte les coordonnées au
 *   parent (écran mairie qui crée/édite un point).
 * - S'ajuste automatiquement à l'emprise des points ; France par défaut si vide.
 */

import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { CollectePoint } from '@/lib/api';
import { COLLECTE_COLOR, COLLECTE_EMOJI, COLLECTE_LABEL } from './collecteTypes';

const IGN_TILES = 'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/png&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}';
const IGN_ATTRIB = '© IGN — Géoplateforme';

interface Props {
  points: CollectePoint[];
  height?: number;
  /** Si fourni, clic sur la carte = pose un marqueur et remonte les coordonnées. */
  onPick?: (coords: { lat: number; lng: number }) => void;
  /** Marqueur temporaire de pose (édition en cours). */
  draft?: { lat: number; lng: number } | null;
}

function markerIcon(color: string, emoji: string): L.DivIcon {
  return L.divIcon({
    className: 'collecte-pin',
    html: `<div style="width:24px;height:24px;transform:translate(-50%,-100%);">
      <div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:${color};
        border:2px solid rgba(255,255,255,.85);transform:rotate(-45deg);
        box-shadow:0 2px 4px rgba(0,0,0,.4);display:grid;place-items:center;">
        <span style="transform:rotate(45deg);font-size:11px;line-height:1;">${emoji}</span>
      </div></div>`,
    iconSize: [24, 24],
    iconAnchor: [0, 0],
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
}

export function CollecteMap({ points, height = 300, onPick, draft }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const draftRef = useRef<L.Marker | null>(null);

  // init
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, { attributionControl: true, zoomControl: true });
    L.tileLayer(IGN_TILES, { attribution: IGN_ATTRIB, maxZoom: 19 }).addTo(map);
    map.setView([46.6, 2.5], 5);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 60);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // clic pour poser (mode mairie)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !onPick) return;
    const handler = (e: L.LeafletMouseEvent) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    map.on('click', handler);
    return () => { map.off('click', handler); };
  }, [onPick]);

  // marqueurs
  useEffect(() => {
    const map = mapRef.current;
    const group = layerRef.current;
    if (!map || !group) return;
    group.clearLayers();

    const withCoords = points.filter((p) => p.latitude !== null && p.longitude !== null);
    for (const p of withCoords) {
      const m = L.marker([p.latitude!, p.longitude!], {
        icon: markerIcon(COLLECTE_COLOR[p.type], COLLECTE_EMOJI[p.type]),
      });
      const accepte = p.dechetsAcceptes.length
        ? `<div style="margin-top:6px;font-size:11px;color:#9aa;">${p.dechetsAcceptes.map(escapeHtml).join(' · ')}</div>`
        : '';
      const horaires = p.horaires ? `<div style="font-size:11px;color:#9aa;">🕒 ${escapeHtml(p.horaires)}</div>` : '';
      const adresse = p.adresse ? `<div style="font-size:11px;color:#9aa;">${escapeHtml(p.adresse)}</div>` : '';
      m.bindPopup(
        `<div style="min-width:150px;">
          <div style="font-weight:600;font-size:13px;">${escapeHtml(p.nom)}</div>
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:${COLLECTE_COLOR[p.type]};">${escapeHtml(COLLECTE_LABEL[p.type])}</div>
          ${adresse}${horaires}${accepte}
        </div>`,
        { closeButton: true }
      );
      m.addTo(group);
    }

    if (withCoords.length > 0) {
      try {
        const bounds = L.latLngBounds(withCoords.map((p) => [p.latitude!, p.longitude!] as [number, number]));
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
      } catch { /* fallback : vue France conservée */ }
    }
    setTimeout(() => map.invalidateSize(), 60);
  }, [points]);

  // marqueur temporaire de pose
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (draftRef.current) { map.removeLayer(draftRef.current); draftRef.current = null; }
    if (draft) {
      const m = L.marker([draft.lat, draft.lng], {
        icon: markerIcon('oklch(0.64 0.14 175)', '✚'),
      });
      m.addTo(map);
      draftRef.current = m;
      map.setView([draft.lat, draft.lng], Math.max(map.getZoom(), 15));
    }
  }, [draft]);

  return (
    <div
      ref={ref}
      style={{ height, borderRadius: 11, overflow: 'hidden' }}
      className="border border-border"
    />
  );
}
